import api, { ApiResponse, extractApiError } from './api';
// ⚠️ **타입 전용 import로 둘 것** — 값으로 가져오면 shared의 exports가 dist라
//    `pnpm --filter @safetyhub/shared build` + dev 재시작·탭 하드 리로드가 묶여 들어온다.
//    여기 필요한 것은 계약뿐이라 타입만 받고, `lib/organizations` 경유를 피해 순환도 만들지 않는다.
import type { VerificationRejectionReason } from '@safetyhub/shared';

// 사용자 타입
export interface User {
  id: string;
  email: string | null;
  /**
   * 연락처 이메일(member.email) — 결재 요청·알림이 실제로 가는 주소.
   * 계정(로그인) 이메일과 **다른 축**이다: 계정 이메일은 로그인 아이디/생명선이고,
   * 이쪽은 사람(Member)에 붙는 업무 연락처라 계정 유형과 무관하게 존재한다.
   * (결정 기록 2026-08-23-contact-email-redefine-and-source-axis-removal)
   */
  contactEmail: string | null;
  username?: string;
  accountType: 'EMAIL' | 'USERNAME' | 'KAKAO' | 'GOOGLE';
  name: string;
  phone: string | null;
  rank?: string | null; // 직급 (사원/대리/과장/부장 등). Phase F-1
  phoneRemovedAt: string | null;  // 다른 계정에서 전화번호 인증으로 해제된 경우
  emailVerified: boolean;
  phoneVerified: boolean;
  isSuperAdmin: boolean;
  /**
   * 전 문서 유형 접근 entitlement — 조직 category와 무관하게 모든 보고서 유형 노출·작성 허용.
   * (결정 기록 2026-08-07-all-report-types-account-entitlement)
   */
  allReportTypes?: boolean;
  createdAt: string;
  memberships: Membership[];
}

/**
 * 사용자의 accountType에 따라 적절한 식별자를 반환합니다.
 */
export function getUserIdentifierFromUser(user: User): string {
  switch (user.accountType) {
    case 'USERNAME':
      return user.username || '-';
    case 'EMAIL':
      return user.email || '-';
    case 'KAKAO':
      return '카카오 로그인';
    case 'GOOGLE':
      return '구글 로그인';
    default:
      return user.email || user.username || '-';
  }
}

export interface Membership {
  id: string;
  companyId: string;
  userId: string;
  rank?: string | null;
  department?: string;
  position?: string;
  /**
   * 조직 RBAC 해소값(줄기 company-rbac P1, shared `MembershipPermissions`) — 서버가 항상 싣는다.
   * 판정은 `permissions`(권한 key)만 본다(`usePermissions().can(key)`). `isOwner`는 소유자 **성질**(마지막 소유자 보호·
   * 조직 삭제·소유권 이전 화면) — P4부터 `usePermissions().isOwner`의 유일한 원천(옛 `role === 'OWNER'` 폴백 없음).
   */
  roleKeys: string[];
  permissions: string[];
  isOwner: boolean;
  createdAt: string;
  /** H-8e: organization 제거 → company 평면화 */
  company: {
    id: string;
    name: string;
    registrationNumber?: string | null;
    category?: 'WORKPLACE' | 'GUIDANCE_AGENCY' | 'SAFETY_MANAGEMENT_AGENCY' | 'WORKSPACE' | null;
    deletedAt?: string | null; // soft delete 시점 (복구 가능 기간 계산용, purge 시 레코드 삭제됨)
    /**
     * 기관 자격 인증 상태 (`VerificationPendingBanner`·`settings/Organization` 등에서 사용).
     *
     * ## ⚠️ 느슨한 것이 «정확하다» — 닫지 말 것 (2026-10-21 판정)
     * shared에 닫힌 union(`VerificationStatus` 9종)이 있는데도 여기를 `string`으로 두는 것은
     * 게으름이 아니라 **이 와이어 값이 실제로 열려 있기 때문**이다:
     * - 쓰기 경로가 **서버 코드**다 — `organizations.service.ts`가 `organization-verification.service`
     *   의 계산값을 그대로 적고, 그 컬럼은 Prisma enum이 아니라 `String @default("NOT_REQUIRED")`다.
     *   즉 **DTO 집행이 0**이고 마이그레이션 없이 새 값이 배포된다(아래 형제
     *   `agencyRejectionReasonCode`와 **정확히 이 점이** 다르다 — 그래서 그쪽은 닫혀 있다).
     * - 그래서 `lib/organizations`의 `verificationBand`가 **`'unknown'` 밴드**를 든다 — 그 설계가
     *   「서버가 먼저 새 상태를 쓰고 web 타입이 낡는 것이 실제 순서」라는 전제 위에 서 있고,
     *   **그 전제의 유일한 표현이 이 `string`이다.** 닫으면 그 분기가 타입상 도달 불가로 보여
     *   다음 사람이 「죽은 코드」로 지운다.
     * - 닫아서 없애는 캐스트도 **0개**다(실측 2026-10-21: `as VerificationStatus`가 web·admin에
     *   0건). 술어 셋이 **의도적으로** 느슨한 입력을 받아 호출부가 캐스트를 안 끼우게 설계돼 있다 —
     *   사유는 `isManualReviewVerificationStatus` JSDoc.
     *
     * **되짚을 조건**: 이 값을 쓰는 서버 경로에 「목록 밖이면 거부」 집행이 생기면(= 형제 사유 코드처럼
     * DTO가 `IsIn`으로 받거나 컬럼이 enum이 되면) 그때 닫는다. 재는 법 —
     * `git grep -n "agencyVerificationStatus" -- apps/server/src/organizations apps/server/src/admin`
     * 의 쓰기 자리가 전부 집행 뒤인지.
     *
     * **그 union의 exhaustive 소유자는 여기가 아니다** —
     * `lib/__tests__/verificationStatus.test.ts`가 `Record<VerificationStatus, …>` 셋(술어·밴드·문구)
     * + **소스 스캔 래칫**으로 분류를 못박는다. 래칫이 필요한 이유도 그 파일 JSDoc에 있다:
     * CI는 web을 Vite(esbuild)로 빌드해 **타입을 보지 않으므로** tsc 강제가 CI에서 돌지 않는다.
     */
    agencyVerificationStatus?: string | null;
    /** 반려 메모(자유 텍스트) — 운영자가 적는다. */
    agencyRejectionReason?: string | null;
    /**
     * 반려 **사유 코드** — 위 형제와 달리 **닫힌 타입이 참이다**.
     *
     * 근거는 데이터가 아니라 **집행**이다: 쓰기 경로가 `RejectVerificationDto.reasonCode`의
     * `@IsIn(VERIFICATION_REJECTION_REASONS)` 뒤이고, 나머지 쓰기 자리는 서버 내부 상수(`'OTHER'`)다.
     * 그 집행을 **재는 검사**는 `apps/server/src/admin/dto/verification.dto.spec.ts`의
     * `[되돌림] reasonCode는 사유 코드 목록 밖이면 거부`다 — 이 타입의 소유자가 그 spec이니,
     * 여기를 다시 `string`으로 되돌리기 전에 그쪽을 먼저 볼 것.
     *
     * ⚠️ **prod 데이터는 이 판정의 근거가 아니다**(2026-10-21 실측: `companies` 85행 중 이 칸이
     * **전부 NULL** — 반려 이력이 0건이라 「목록 밖 0건」은 **분모가 0이라 공허**하다).
     *
     * 닫은 효과: `REJECTION_REASON_LABELS[...]` 조회부의 캐스트 2개가 사라졌다
     * (`VerificationPendingBanner`·`settings/Organization`). 그 캐스트가 **다음 드리프트의 입구**였다 —
     * 캐스트는 호출부마다 다시 만들어지고 매번 「무엇으로 좁혔나」가 갈릴 수 있다.
     */
    agencyRejectionReasonCode?: VerificationRejectionReason | null;
    /**
     * 업로드된 자격 서류 jsonb — **좁히지 않는다.** 스키마가 이 칸을 「전환 기간의 **거울**」이라
     * 적고 정본은 `AgencyVerificationDocument` 테이블이다(schema.prisma의 그 모델 JSDoc). 형상이
     * 한 벌이 아닐 수 있으므로 **좁히는 자리는 타입이 아니라 런타임 가드**다
     * (`VerificationPendingBanner`의 `Array.isArray`).
     */
    agencyVerificationDocuments?: unknown;
    groups?: import('./groups').Group[];
  };
  siteAssignments?: SiteAssignment[];
  /** 이 멤버가 소속된 그룹들 — 비Owner 셀렉터의 가시 범위 산출에 쓴다. */
  groupMembers?: { groupId: string; role: string }[];
}

export interface SiteAssignment {
  siteId: string;
  site: {
    id: string;
    name: string;
  };
}

// 인증 응답 타입 (signup/login)
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthResponse {
  id: string;
  email: string;
  name: string;
  tokens: AuthTokens;
  accountRestored?: boolean;
}

// 탈퇴(SELF) 유예 기간 내 로그인 시 — 정식 토큰 대신 복원 대기 응답
export interface RestorePendingResponse {
  restorePending: true;
  daysLeft: number;
  restoreToken: string;
}

// 로그인 요청 (identifier: 이메일 또는 아이디)
export interface LoginRequest {
  identifier: string;
  password: string;
}

// USERNAME 타입 회원가입 요청
export interface SignupUsernameRequest {
  username: string;
  password: string;
  name: string;
  phone: string;
  phoneVerificationToken: string;
  email?: string;
  emailVerificationToken?: string;
  timezone?: string;
  inviteToken?: string;
}

// EMAIL 타입 가입 시작 응답
export interface EmailInitiateResponse {
  message: string;
  expiresAt: string;
}

// EMAIL 타입 회원가입 완료 요청
export interface SignupEmailRequest {
  email: string;
  emailVerificationToken?: string; // 초대를 통한 가입 시 생략 가능 (inviteToken으로 대체)
  password: string;
  name: string;
  phone: string;
  phoneVerificationToken: string;
  timezone?: string;
  inviteToken?: string;
}

// 인증 발송/검증 타입
export type VerificationType = 'PHONE' | 'EMAIL_SIGNUP' | 'EMAIL_VERIFY';

// 인증 발송 요청
export interface SendVerificationRequest {
  type: VerificationType;
  phone?: string;
  email?: string;
}

// 인증 발송 응답
export interface SendVerificationResponse {
  expiresAt: string;
}

// 인증 코드 검증 요청
export interface VerifyCodeRequest {
  type: 'PHONE' | 'EMAIL_VERIFY';
  phone?: string;
  email?: string;
  code: string;
}

// 인증 코드 검증 응답
export interface VerifyCodeResponse {
  verificationToken: string;
}

// 이메일 토큰 검증 응답
export interface VerifyEmailTokenResponse {
  email: string;
  verificationToken: string;
}

// 중복 확인 응답
export interface CheckDuplicateResponse {
  available: boolean;
  message?: string;
}

// 연결된 인증 수단 타입
export interface AuthMethod {
  id: string;
  provider: 'EMAIL' | 'ID' | 'KAKAO' | 'GOOGLE';
  providerId: string;
  providerIdMasked: string;
  verified: boolean;
  linkedAt: string;
  lastUsedAt: string | null;
}

// 계정 통합 관련 타입
export type MergeIdentifierType = 'EMAIL' | 'USERNAME' | 'PHONE';

export interface InitiateMergeRequest {
  identifierType: MergeIdentifierType;
  identifier: string;
  password: string; // 본인 비밀번호 — identify 단계에서 선검증
}

export interface InitiateMergeResponse {
  requestId: string;
  message: string;
  verificationMethod: 'SMS' | 'EMAIL';
  expiresAt?: string;
}

export interface VerifyMergeRequest {
  requestId: string;
  code: string;
}

export interface AuthMethodInfo {
  id: string;
  provider: 'EMAIL' | 'ID' | 'KAKAO' | 'GOOGLE';
  providerIdMasked: string;
  isPasswordAuth: boolean;
}

export interface RoleConflict {
  organizationName: string;
  primaryRole: string;
  secondaryRole: string;
  resolvedRole: string;
}

export interface VerifyMergeResponse {
  verified: boolean;
  requestId: string;
  message: string;
  phoneConflict?: boolean;
  primaryPhone?: string;
  secondaryPhone?: string;
  socialAuthMethods?: AuthMethodInfo[];
  roleConflicts?: RoleConflict[];
  confirmExpiresAt?: string;
}

export interface ResendMergeResponse {
  requestId: string;
  verificationMethod: 'SMS' | 'EMAIL';
  expiresAt: string;
}

export interface ConfirmMergeRequest {
  requestId: string;
  selectedPhone?: string;
  // password는 initiate 단계로 이동 — confirm body에서 제거
}

export interface ConfirmMergeResponse {
  message: string;
  mergedAuthCount: number;
  mergedOrganizationCount: number;
}

export interface MergeRequest {
  id: string;
  primaryUserId: string;
  secondaryUserId: string;
  status: 'PENDING_VERIFICATION' | 'VERIFIED' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  createdAt: string;
  primaryUser: { id: string; name: string };
  secondaryUser: { id: string; name: string };
}

// 인증 API
export const authApi = {
  // 로그인 (identifier: 이메일 또는 아이디)
  // 탈퇴(SELF) 유예 기간 내 계정은 restorePending: true 응답 반환 (토큰 없음)
  login: async (data: LoginRequest): Promise<
    { user: User; tokens: AuthTokens; accountRestored?: boolean } | RestorePendingResponse
  > => {
    const response = await api.post<ApiResponse<AuthResponse | RestorePendingResponse>>('/auth/login', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '로그인에 실패했습니다.');
    }
    const authData = response.data.data;

    // 탈퇴 유예 기간 내 계정 — 복원 확인 모달로 분기
    if ('restorePending' in authData && authData.restorePending) {
      return authData as RestorePendingResponse;
    }

    const normalAuthData = authData as AuthResponse;

    // 로그인 후 사용자 전체 정보 조회
    const meResponse = await api.get<ApiResponse<User>>('/auth/me', {
      headers: {
        Authorization: `Bearer ${normalAuthData.tokens.accessToken}`,
      },
    });

    if (!meResponse.data.success || !meResponse.data.data) {
      throw new Error('사용자 정보를 가져올 수 없습니다.');
    }

    return {
      user: meResponse.data.data,
      tokens: normalAuthData.tokens,
      accountRestored: normalAuthData.accountRestored,
    };
  },

  // 계정 복원 확정 (restoreToken으로 인증 → 정식 로그인 응답)
  restore: async (restoreToken: string): Promise<{ user: User; tokens: AuthTokens }> => {
    // body는 빈 객체로 — axios가 body=null일 때 요청이 CORS preflight에서 깨져 ERR_NETWORK 발생
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/restore', {}, {
      headers: {
        Authorization: `Bearer ${restoreToken}`,
      },
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '계정 복원에 실패했습니다.');
    }
    const authData = response.data.data;

    // 복원 후 사용자 전체 정보 조회
    const meResponse = await api.get<ApiResponse<User>>('/auth/me', {
      headers: {
        Authorization: `Bearer ${authData.tokens.accessToken}`,
      },
    });

    if (!meResponse.data.success || !meResponse.data.data) {
      throw new Error('사용자 정보를 가져올 수 없습니다.');
    }

    return {
      user: meResponse.data.data,
      tokens: authData.tokens,
    };
  },

  // EMAIL 타입 가입 시작 (이메일 발송)
  initiateEmailSignup: async (email: string): Promise<EmailInitiateResponse> => {
    const response = await api.post<ApiResponse<EmailInitiateResponse>>('/auth/email/initiate', {
      email,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '이메일 발송에 실패했습니다.');
    }
    return response.data.data;
  },

  // EMAIL 타입 회원가입 완료
  signupWithEmail: async (data: SignupEmailRequest): Promise<{ user: User; tokens: AuthTokens }> => {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/signup/email', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '회원가입에 실패했습니다.');
    }
    const authData = response.data.data;

    const meResponse = await api.get<ApiResponse<User>>('/auth/me', {
      headers: {
        Authorization: `Bearer ${authData.tokens.accessToken}`,
      },
    });

    if (!meResponse.data.success || !meResponse.data.data) {
      throw new Error('사용자 정보를 가져올 수 없습니다.');
    }

    return {
      user: meResponse.data.data,
      tokens: authData.tokens,
    };
  },

  // USERNAME 타입 회원가입
  signupWithUsername: async (data: SignupUsernameRequest): Promise<{ user: User; tokens: AuthTokens }> => {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/signup/username', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '회원가입에 실패했습니다.');
    }
    const authData = response.data.data;

    const meResponse = await api.get<ApiResponse<User>>('/auth/me', {
      headers: {
        Authorization: `Bearer ${authData.tokens.accessToken}`,
      },
    });

    if (!meResponse.data.success || !meResponse.data.data) {
      throw new Error('사용자 정보를 가져올 수 없습니다.');
    }

    return {
      user: meResponse.data.data,
      tokens: authData.tokens,
    };
  },

  // 로그아웃
  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      // 로그아웃 실패해도 로컬 토큰은 삭제
    }
  },

  // 현재 사용자 정보 조회
  getMe: async (): Promise<User> => {
    const response = await api.get<ApiResponse<User>>('/auth/me');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '사용자 정보를 가져올 수 없습니다.');
    }
    return response.data.data;
  },

  // 토큰 갱신
  refresh: async (refreshToken: string): Promise<AuthTokens> => {
    const response = await api.post<ApiResponse<AuthTokens>>('/auth/refresh', {
      refreshToken,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '토큰 갱신에 실패했습니다.');
    }
    return response.data.data;
  },

  // 비밀번호 재설정 요청 (이메일)
  requestPasswordReset: async (email: string): Promise<{ message: string; expiresAt: string }> => {
    const response = await api.post<ApiResponse<{ message: string; expiresAt: string }>>(
      '/auth/password-reset/request',
      { email }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '비밀번호 재설정 요청에 실패했습니다.');
    }
    return response.data.data;
  },

  // 비밀번호 재설정 완료 (이메일 토큰)
  verifyPasswordReset: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>(
      '/auth/password-reset/verify',
      { token, newPassword }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '비밀번호 재설정에 실패했습니다.');
    }
    return response.data.data;
  },

  // 비밀번호 재설정 요청 (SMS)
  requestPasswordResetBySms: async (phone: string): Promise<{ message: string; expiresAt: string }> => {
    const response = await api.post<ApiResponse<{ message: string; expiresAt: string }>>(
      '/auth/password-reset/sms/request',
      { phone }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '인증 코드 발송에 실패했습니다.');
    }
    return response.data.data;
  },

  // 비밀번호 재설정 완료 (SMS 코드)
  verifyPasswordResetBySms: async (
    phone: string,
    code: string,
    newPassword: string
  ): Promise<{ message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>(
      '/auth/password-reset/sms/verify',
      { phone, code, newPassword }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '비밀번호 재설정에 실패했습니다.');
    }
    return response.data.data;
  },

  // 연결된 인증 수단 목록 조회
  getAuthMethods: async (): Promise<AuthMethod[]> => {
    const response = await api.get<ApiResponse<AuthMethod[]>>('/auth/methods');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '인증 수단 조회에 실패했습니다.');
    }
    return response.data.data;
  },

  // 소셜 로그인 연결 해제
  unlinkAuthMethod: async (authId: string, password?: string): Promise<void> => {
    const response = await api.delete<ApiResponse<null>>(`/auth/methods/${authId}`, {
      data: { password },
    });
    if (!response.data.success) {
      throw new Error(response.data.error?.message || '연결 해제에 실패했습니다.');
    }
  },

  // 소셜 로그인 연결
  linkSocialAuth: async (
    provider: 'KAKAO' | 'GOOGLE',
    code: string,
    redirectUri: string,
  ): Promise<{ authId: string; provider: string; providerIdMasked: string }> => {
    const response = await api.post<ApiResponse<{ authId: string; provider: string; providerIdMasked: string }>>(
      '/auth/link',
      { provider, code, redirectUri },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '소셜 로그인 연결에 실패했습니다.');
    }
    return response.data.data;
  },

  // OAuth URL 생성 (카카오)
  getKakaoAuthUrl: (redirectUri: string): string => {
    const clientId = import.meta.env.VITE_KAKAO_CLIENT_ID;
    if (!clientId) return '';
    return `https://kauth.kakao.com/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code`;
  },

  // OAuth URL 생성 (구글)
  getGoogleAuthUrl: (redirectUri: string): string => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return '';
    return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile`;
  },
};

// 인증(검증) API
export const verificationApi = {
  // 인증 코드 발송 (SMS 또는 이메일)
  send: async (data: SendVerificationRequest): Promise<SendVerificationResponse> => {
    const response = await api.post<ApiResponse<SendVerificationResponse>>('/verification/send', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '인증 코드 발송에 실패했습니다.');
    }
    return response.data.data;
  },

  // 인증 코드 검증
  verify: async (data: VerifyCodeRequest): Promise<VerifyCodeResponse> => {
    const response = await api.post<ApiResponse<VerifyCodeResponse>>('/verification/verify', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '인증에 실패했습니다.');
    }
    return response.data.data;
  },

  // 이메일 가입 토큰 검증
  verifyEmailToken: async (token: string): Promise<VerifyEmailTokenResponse> => {
    const response = await api.get<ApiResponse<VerifyEmailTokenResponse>>(`/verification/email/verify/${token}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '이메일 인증에 실패했습니다.');
    }
    return response.data.data;
  },

  // 아이디 중복 확인
  checkUsername: async (username: string): Promise<CheckDuplicateResponse> => {
    const response = await api.get<ApiResponse<CheckDuplicateResponse>>(`/verification/check/username/${username}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '확인에 실패했습니다.');
    }
    return response.data.data;
  },

  // 이메일 중복 확인
  checkEmail: async (email: string): Promise<CheckDuplicateResponse> => {
    const response = await api.get<ApiResponse<CheckDuplicateResponse>>(`/verification/check/email/${encodeURIComponent(email)}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '확인에 실패했습니다.');
    }
    return response.data.data;
  },

  // 휴대폰 중복 확인
  checkPhone: async (phone: string): Promise<CheckDuplicateResponse> => {
    const response = await api.get<ApiResponse<CheckDuplicateResponse>>(`/verification/check/phone/${encodeURIComponent(phone)}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '확인에 실패했습니다.');
    }
    return response.data.data;
  },

  // 비밀번호 재설정 토큰 유효성 확인
  checkPasswordResetToken: async (token: string): Promise<{ valid: boolean; email?: string }> => {
    const response = await api.get<ApiResponse<{ valid: boolean; email?: string }>>(`/verification/password-reset/check/${encodeURIComponent(token)}`);
    if (!response.data.success || !response.data.data) {
      return { valid: false };
    }
    return response.data.data;
  },

  // 이메일 가입 토큰 유효성 확인
  checkEmailSignupToken: async (token: string): Promise<{ valid: boolean; email?: string }> => {
    const response = await api.get<ApiResponse<{ valid: boolean; email?: string }>>(`/verification/email-signup/check/${encodeURIComponent(token)}`);
    if (!response.data.success || !response.data.data) {
      return { valid: false };
    }
    return response.data.data;
  },
};

// 계정 통합 API
export const accountMergeApi = {
  // 통합 요청 시작
  initiate: async (data: InitiateMergeRequest): Promise<InitiateMergeResponse> => {
    const response = await api.post<ApiResponse<InitiateMergeResponse>>('/account-merge/initiate', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '통합 요청에 실패했습니다.');
    }
    return response.data.data;
  },

  // 대상 계정 인증
  verify: async (data: VerifyMergeRequest): Promise<VerifyMergeResponse> => {
    const response = await api.post<ApiResponse<VerifyMergeResponse>>('/account-merge/verify', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '인증에 실패했습니다.');
    }
    return response.data.data;
  },

  // 통합 확정
  confirm: async (data: ConfirmMergeRequest): Promise<ConfirmMergeResponse> => {
    const response = await api.post<ApiResponse<ConfirmMergeResponse>>('/account-merge/confirm', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '통합 확정에 실패했습니다.');
    }
    return response.data.data;
  },

  // 인증 코드 재발송
  resend: async (requestId: string): Promise<ResendMergeResponse> => {
    const response = await api.post<ApiResponse<ResendMergeResponse>>(`/account-merge/${requestId}/resend`, {});
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '재발송에 실패했습니다.');
    }
    return response.data.data;
  },

  // 통합 요청 취소
  cancel: async (requestId: string): Promise<void> => {
    const response = await api.delete<ApiResponse<null>>(`/account-merge/${requestId}`);
    if (!response.data.success) {
      throw new Error(response.data.error?.message || '취소에 실패했습니다.');
    }
  },

  // 통합 요청 목록 조회
  getRequests: async (): Promise<MergeRequest[]> => {
    const response = await api.get<ApiResponse<MergeRequest[]>>('/account-merge');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '조회에 실패했습니다.');
    }
    return response.data.data;
  },
};

export { extractApiError };
